import clsx from 'clsx';
import type { ReactNode } from 'react';
import { useId } from 'react';
import styles from './Field.module.css';

export interface FieldControlProps {
  readonly id: string;
  readonly 'aria-describedby': string | undefined;
  readonly 'aria-invalid': true | undefined;
}

export interface FieldProps {
  readonly label: string;
  /** Short format hint under the control; replaced by the error when there is one. */
  readonly hint?: ReactNode | undefined;
  readonly error?: string | null | undefined;
  /** Text at the label's end, e.g. a character counter. */
  readonly aside?: ReactNode | undefined;
  /** The label is announced but not drawn, for controls whose purpose is obvious from context. */
  readonly labelHidden?: boolean | undefined;
  readonly className?: string | undefined;
  readonly children: (control: FieldControlProps) => ReactNode;
}

/** Label, control, hint and error wired together for assistive technology. */
export const Field = ({
  label,
  hint,
  error,
  aside,
  labelHidden = false,
  className,
  children,
}: FieldProps) => {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error || hint;
  return (
    <div className={clsx(styles.field, className)}>
      <div className={clsx(styles.labelRow, labelHidden && styles.hidden)}>
        <label className={styles.label} htmlFor={id}>
          {label}
        </label>
        {aside && <span className={styles.aside}>{aside}</span>}
      </div>
      {children({
        id,
        'aria-describedby': message ? messageId : undefined,
        'aria-invalid': error ? true : undefined,
      })}
      {message && (
        <p
          id={messageId}
          className={clsx(styles.message, error && styles.error)}
          role={error ? 'alert' : undefined}
        >
          {message}
        </p>
      )}
    </div>
  );
};

/** A labelled group of controls that are not a single input (segmented controls, switches). */
export const FieldGroup = ({
  label,
  children,
  className,
}: {
  readonly label: string;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}) => (
  <fieldset className={clsx(styles.field, styles.group, className)}>
    <legend className={styles.label}>{label}</legend>
    {children}
  </fieldset>
);
